import { Col, Container, Row } from 'reactstrap'
import { ActorBock } from './components/ActorBlock'
import { Header } from './components/Header'
import { EditActorModal, EDIT_ACTOR_MODAL } from './components/modals/EditActorModal'
import { EditAssignmentModal, EDIT_ASSIGNMENT_MODAL } from './components/modals/EditAssignmentModal'
import { ExportErtTimestamps, EXPORT_ERT_TIMESTAMPS_MODAL } from './components/modals/ExportErtTimestamps'
import { ImportModal, IMPORT_MODAL } from './components/modals/ImportModal'
import { TimelineCanvas } from './components/TimelineCanvas'
import { RENDER_CONSTS } from './config/constants'
import { SubHeader } from './containers/SubHeader'
import { useAppDispatch, useAppSelector } from './redux/redux-hooks'
import { ModalsActions } from './redux/slices/modals-slice'
import { ViewTimelineActions } from './redux/slices/view-timeline-slice'
import { expectOneById, extractOneFromList, getListFor, splitList } from './util/utils'

export const App = (): JSX.Element => {

    const appDisp = useAppDispatch()
    const timelineState = useAppSelector(state => state.viewTimeline)
    const modals = useAppSelector(state => state.modals)

    const fightData = timelineState.editingFight

    const { match: bossActor, restOfList: playerActors } = extractOneFromList(
        fightData.actors,
        (actor) => actor.isBoss
    )

    const { taken: hiddenActors, remaining: visibleActors } = splitList(
        playerActors,
        (actor) => actor.hidden
    )

    const sortedActors = [bossActor, ...visibleActors, ...hiddenActors]
    const canvasActors = [bossActor, ...visibleActors]

    const actorBlocks = sortedActors.map((actor, index) => {

        const spells = getListFor(fightData.spells, (s) => s.actorId === actor.id)

        return (
            <ActorBock
                key={actor.id}
                actor={actor}
                spells={actor.hidden ? [] : spells}
                index={index}

                onSpellRowClicked={(spell) => {
                    appDisp(ModalsActions.openExportTimestampsModal({
                        actorName: actor.name,
                        spell: spell,
                        assignments: fightData.assignments.filter(ass => ass.spellId === spell.id)
                    }))
                }}

                onEditActor={() => {
                    appDisp(ModalsActions.openEditActorModal({
                        actor,
                        spells
                    }))
                }}

                onChangeOrdinal={(dir) => {
                    appDisp(ViewTimelineActions.changeActorOrdinal({
                        actorId: actor.id,
                        dir
                    }))
                }}
            />
        )
    })

    const setFightName = (name: string) => {
        appDisp(ViewTimelineActions.setFightName({ name }))
    }

    const selectFightToEdit = (ev: any) => {
        appDisp(ViewTimelineActions.selectFight({ fightId: ev.target.value }))
    }

    const saveFightEdits = () => {
        appDisp(ViewTimelineActions.saveFightChanges())
    }

    const createNewFight = () => {
        appDisp(ViewTimelineActions.createFight({
            name: "Untitled"
        }))
    }

    const cloneFight = () => {
        appDisp(ViewTimelineActions.cloneFight())
    }

    const deleteFight = () => {
        appDisp(ViewTimelineActions.deleteFight())
    }

    const importFight = () => {
        appDisp(ModalsActions.openImportModal({}))
    }
    const exportFight = () => {
        navigator.clipboard.writeText(JSON.stringify(fightData))
        alert('done, pushed to clipboard')
    }

    return (
        <Container fluid>

            {modals.openModals.map((modal, index) => {
                if (modal.type === EDIT_ACTOR_MODAL) {
                    return (
                        <EditActorModal key={index} {...modal.options} />
                    )
                }

                if (modal.type === EDIT_ASSIGNMENT_MODAL) {
                    return (
                        <EditAssignmentModal key={index} {...modal.options} />
                    )
                }

                if (modal.type === IMPORT_MODAL) {
                    return (
                        <ImportModal key={index} {...modal.options} />
                    )
                }

                if (modal.type === EXPORT_ERT_TIMESTAMPS_MODAL) {
                    return (
                        <ExportErtTimestamps key={index} {...modal.options} />
                    )
                }

                throw Error(`Unmapped modal ${modal.type}`)
            })}

            <Row className="border-bottom">
                <Col sm={12} className="d-flex space-mx-2 align-items-end my-3">
                    <Header
                        key={fightData.fight.id}

                        fightData={fightData}
                        storedFights={timelineState.storedFights}

                        cloneFight={cloneFight}
                        deleteFight={deleteFight}
                        selectFightToEdit={selectFightToEdit}
                        commitFightEdits={saveFightEdits}
                        commitFightName={setFightName}
                        createNewFight={createNewFight}

                        importFight={importFight}
                        exportFight={exportFight}
                    />
                </Col>
            </Row>

            <Row className="border-bottom">
                <Col sm={12} className="d-flex space-mx-2 align-items-end my-3">
                    <SubHeader />
                </Col>
            </Row>

            <Row>

                <Col sm={12}>

                    <div className="d-flex py-3">
                        <div
                            className="d-flex flex-column"
                            style={{
                                // No idea what these 2 pixels are,
                                // but we need them to bump this down
                                // to account for the timeline
                                marginTop: RENDER_CONSTS.LANES_Y_OFFSET + 2,
                                marginRight: "-1px"
                            }}
                        >
                            {actorBlocks}
                        </div>

                        <div className="bg-white d-flex w-100">
                            <TimelineCanvas
                                fightData={{
                                    ...fightData,
                                    actors: canvasActors
                                }}
                                hiddenActorsCount={hiddenActors.length}
                                onCreateAssignment={({ spellId, offsetSec }) => {
                                    appDisp(ViewTimelineActions.createNewAssignment({
                                        spellId,
                                        offset: offsetSec
                                    }))
                                }}
                                onAssignmentMoved={({ assignmentId, offsetSec }) => {
                                    appDisp(ViewTimelineActions.updateAssignmentOffset({
                                        assignmentId,
                                        offset: offsetSec
                                    }))
                                }}
                                onRightClickAssignment={({ assignmentId }) => {

                                    const spellAssignment = expectOneById(fightData.assignments, assignmentId)
                                    const spell = expectOneById(fightData.spells, spellAssignment.spellId)

                                    appDisp(ModalsActions.openEditAssignmentModal({
                                        spell,
                                        spellAssignment,
                                    }))

                                }}
                            />
                            {/* <TimelineCanvasV2 fight={fight} /> */}
                        </div>
                    </div>

                </Col>

            </Row>

        </Container>
    )
}
